/**
 * Eén groepje in het resultatenoverzicht: wie, hoe ver, de rolverdeling
 * die het model maakte, het gesprek (op verzoek), de hulp die Socrates
 * gaf, en de ingeleverde argumenten.
 *
 * Dit is wat er van een les overblijft, en wat het onderzoek gebruikt.
 * Daarom staat er ook bij een rolverdeling wie hem maakte en waarom, en
 * bij een tegengehouden bericht wat de leerling had getypt.
 */
import React, { useState } from 'react';
import { kleur } from '../../../theme/tokens';

export function ResultaatKaart({ resultaat: r }) {
  return (
    <div style={styles.resultCard}>
      <div style={styles.resultHeader}>
        <span style={styles.resultAvatar}>{r.groepje_avatar}</span>
        <div>
          <div style={styles.resultNaam}>{r.groepje_naam}</div>
          <div style={styles.resultMeta}>{r.students.join(', ')} • {r.chat_count} berichten</div>
        </div>
        <span style={r.status === 'klaar' ? styles.statusKlaar : styles.statusBezig}>
          {r.status === 'klaar' ? '✅ Klaar' : '⏳ Bezig'}
        </span>
      </div>
      {r.rollen?.length > 0 && <Rolverdeling resultaat={r} />}
      {r.gesprek?.length > 0 && <GesprekLog resultaat={r} />}
      {r.socrates_hulp?.length > 0 && <HulpLog hulp={r.socrates_hulp} />}
      {r.status === 'klaar' && (
        <div style={styles.argumentenBox}>
          <div style={styles.argumentenVoor}>
            <strong>✅ VOOR:</strong>
            <ul>{r.argumenten_voor.map((a, i) => <li key={i}>{a}</li>)}</ul>
          </div>
          <div style={styles.argumentenTegen}>
            <strong>❌ TEGEN:</strong>
            <ul>{r.argumenten_tegen.map((a, i) => <li key={i}>{a}</li>)}</ul>
          </div>
        </div>
      )}
    </div>
  );
}

/** Welk personage welke rol kreeg, door wie, en met welke onderbouwing. */
function Rolverdeling({ resultaat: r }) {
  return (
    <div style={styles.blok}>
      <div style={styles.blokKop}>
        Rolverdeling
        <span style={styles.blokBron}>
          {r.rolverdeling_bron === 'llm'
            ? `door ${r.rolverdeling_model || 'het model'}`
            : `op volgorde${r.rolverdeling_reden ? ' — ' + r.rolverdeling_reden : ''}`}
        </span>
      </div>
      <ul style={styles.lijst}>
        {r.rollen.map((rol, i) => (
          <li key={i} style={styles.regel}>
            <strong>{rol.naam}</strong>
            <span style={styles.rolNaam}>{rol.rol}</span>
            {rol.vast && <span style={styles.rolVast}>vaste rol</span>}
            {rol.waarom && <span style={styles.waarom}>{rol.waarom}</span>}
          </li>
        ))}
      </ul>
      {r.rolverdeling_weggevallen?.length > 0 && (
        <p style={styles.weggevallen}>
          Niet verdeeld: {r.rolverdeling_weggevallen.join(', ')} — dit groepje koos een
          personage met een vaste rol, dat nam een plek in.
        </p>
      )}
    </div>
  );
}

/** Het gesprek, dichtgeklapt tot de leerkracht wil meelezen. */
function GesprekLog({ resultaat: r }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.blok}>
      <div style={styles.blokKop}>
        Het gesprek
        <span style={styles.blokBron}>{r.chat_count} berichten</span>
        <button onClick={() => setOpen(!open)} style={styles.gesprekKnop}>{open ? 'verberg' : 'lees mee'}</button>
      </div>
      {open && (
        <div style={styles.gesprekLog}>
          {r.gesprek.map((m, i) => (
            <div key={i} style={m.type === 'taalcorrectie' ? styles.gesprekCorrectie : styles.gesprekRegel}>
              <span style={styles.gesprekVan}>{m.van}</span>
              <span>{m.bericht}</span>
              {m.geblokkeerd && (
                <div style={styles.gesprekGeblokkeerd}>
                  tegengehouden bericht van de leerling: “{m.geblokkeerd}”
                  {m.termen?.length > 0 && ' — ' + m.termen.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Welke hulp het groepje in de tweede fase inschakelde, en wat Socrates zei. */
function HulpLog({ hulp }) {
  return (
    <div style={styles.blok}>
      <div style={styles.blokKop}>
        Hulp van Socrates
        <span style={styles.blokBron}>{hulp.length}x ingeschakeld</span>
      </div>
      <ul style={styles.lijst}>
        {hulp.map((h, i) => (
          <li key={i} style={styles.regel}>
            <span style={styles.rolNaam}>{h.soort}</span>
            {h.tekst && <span style={styles.hulpOverTekst}>over: “{h.tekst}”</span>}
            <span style={styles.waarom}>{h.fout ? 'niet gelukt: ' + h.fout : h.antwoord}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const styles = {
  resultCard: { background: kleur.vlak, borderRadius: '12px', padding: '16px' },
  resultHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  resultAvatar: { fontSize: '32px' },
  resultNaam: { fontWeight: 'bold', fontSize: '16px' },
  resultMeta: { fontSize: '12px', color: kleur.stil },
  statusKlaar: { background: kleur.accent, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', marginLeft: 'auto' },
  statusBezig: { background: kleur.oranje, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', marginLeft: 'auto' },
  blok: { background: kleur.vlakKoel, borderRadius: '8px', padding: '12px 14px', margin: '10px 0' },
  blokKop: { fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' },
  blokBron: { fontWeight: 'normal', color: kleur.tekstStil, fontSize: '12px' },
  lijst: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
  regel: { fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' },
  rolNaam: { background: kleur.accentLicht, color: kleur.accentTekst, padding: '2px 8px', borderRadius: '10px', fontSize: '12px' },
  rolVast: { color: kleur.grijs, fontSize: '12px' },
  waarom: { color: kleur.tekstZacht, fontStyle: 'italic', flexBasis: '100%' },
  hulpOverTekst: { color: kleur.tekstStil, fontSize: '12px' },
  weggevallen: { fontSize: '12px', color: kleur.amberTekst, background: kleur.amber, border: `1px solid ${kleur.amberLijn}`, borderRadius: '8px', padding: '6px 10px', margin: '8px 0 0' },
  gesprekKnop: { marginLeft: 'auto', border: `1px solid ${kleur.accent}`, background: 'white', color: kleur.accentTekst, borderRadius: '8px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', font: 'inherit' },
  gesprekLog: { maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' },
  gesprekVan: { fontWeight: 'bold', marginRight: '6px' },
  gesprekRegel: { fontSize: '13px', lineHeight: 1.5 },
  gesprekCorrectie: { fontSize: '13px', lineHeight: 1.5, background: kleur.amber, border: `1px solid ${kleur.amberLijn}`, borderRadius: '8px', padding: '8px 10px' },
  gesprekGeblokkeerd: { color: kleur.amberTekst, fontSize: '12px', marginTop: '4px' },
  argumentenBox: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' },
  argumentenVoor: { background: kleur.accentLichter, padding: '12px', borderRadius: '8px', fontSize: '14px' },
  argumentenTegen: { background: kleur.roodLicht, padding: '12px', borderRadius: '8px', fontSize: '14px' },
};
